import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Keypad } from '@/components/Keypad';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Zap, CheckCircle, XCircle, Loader2, Copy,
  Monitor, AlertTriangle, CreditCard, Maximize, QrCode, ChevronDown, ChevronUp
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { playSuccessSound, warmUpAudio } from '@/lib/sound';
import { printQRFromDOM } from '@/lib/printQR';
import { useAuth } from '@/contexts/AuthContext';

export default function NewPayment() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [stripeConnected, setStripeConnected] = useState(null);
  const [showDisplayLink, setShowDisplayLink] = useState(false);
  const [feeData, setFeeData] = useState(null);
  const [showFees, setShowFees] = useState(false);
  const [showFixedQR, setShowFixedQR] = useState(false);

  const fixedQRUrl = user ? `${window.location.origin}/pay/${user.store_id}/${user.user_id}` : '';

  useEffect(() => {
    api.get('/stripe/status').then(res => {
      setStripeConnected(res.data.connected);
    }).catch(() => setStripeConnected(false));
  }, []);

  useEffect(() => {
    if (location.state?.amount) {
      setAmount(String(location.state.amount));
      setDescription(location.state.description || '');
    }
  }, [location.state]);

  const numericAmount = parseFloat(amount || '0');
  const formattedAmount = numericAmount.toFixed(2);
  const displayUrl = transactionId ? `${window.location.origin}/display/${transactionId}` : '';

  // Fetch fee estimation when amount changes (debounced)
  useEffect(() => {
    if (numericAmount <= 0) { setFeeData(null); return; }
    const timer = setTimeout(() => {
      api.get(`/fees/estimate?amount=${numericAmount}`).then(res => setFeeData(res.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [numericAmount]);

  const handleCharge = async () => {
    if (numericAmount <= 0) {
      toast.error('Enter an amount greater than zero');
      return;
    }
    // Warm up audio context on user gesture so sound works when payment completes
    warmUpAudio();
    setLoading(true);
    try {
      const res = await api.post('/payments/create', {
        amount: numericAmount,
        origin_url: window.location.origin,
        description
      });
      setCheckoutUrl(res.data.checkout_url);
      setSessionId(res.data.session_id);
      setTransactionId(res.data.transaction_id);
      setPaymentStatus('pending');
      setShowQR(true);
      setShowDisplayLink(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create payment');
    } finally {
      setLoading(false);
    }
  };

  const pollStatus = useCallback(async () => {
    if (!sessionId || paymentStatus === 'paid') return;
    try {
      const res = await api.get(`/payments/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setPaymentStatus('paid');
        playSuccessSound();
        toast.success('Payment received!');
      } else if (res.data.payment_status === 'failed' || res.data.status === 'expired') {
        setPaymentStatus('failed');
      }
    } catch { /* ignore */ }
  }, [sessionId, paymentStatus]);

  useEffect(() => {
    if (!showQR || paymentStatus === 'paid' || paymentStatus === 'failed') return;
    const interval = setInterval(pollStatus, 2500);
    return () => clearInterval(interval);
  }, [showQR, paymentStatus, pollStatus]);

  const resetPayment = () => {
    setShowQR(false);
    setCheckoutUrl('');
    setSessionId('');
    setTransactionId('');
    setPaymentStatus('pending');
    setAmount('');
    setDescription('');
    setShowDisplayLink(false);
  };

  const isBlocked = stripeConnected === false;

  return (
    <div className="max-w-md mx-auto animate-fade-in-up" data-testid="new-payment-page">
      <div className="mb-4">
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          New Payment
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Enter amount and generate QR code</p>
      </div>

      {/* Stripe blocker */}
      {isBlocked && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-6 mb-4 text-center" data-testid="stripe-required-blocker">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-base font-bold text-amber-800 dark:text-amber-300 mb-1">Connect your Stripe account</h2>
          <p className="text-sm text-amber-600 dark:text-amber-400 mb-4">
            You must connect your Stripe account before accepting payments. Payments go directly to your bank account via Stripe.
          </p>
          <Button onClick={() => navigate('/settings')} className="h-11 bg-[#635BFF] hover:bg-[#5851DB] text-white font-semibold" data-testid="connect-stripe-payment-btn">
            <CreditCard className="w-4 h-4 mr-2" /> Connect Stripe Account
          </Button>
        </div>
      )}

      <div className={isBlocked ? 'opacity-40 pointer-events-none' : ''}>
        {/* Quick amounts */}
        <div className="grid grid-cols-4 gap-1.5 mb-0" data-testid="quick-amounts">
          {[5, 10, 20, 50].map(val => (
            <button
              key={val}
              onClick={() => setAmount(String(val))}
              className="h-12 sm:h-11 bg-card border border-border text-sm font-bold text-foreground hover:bg-muted active:scale-95 transition-all"
              data-testid={`quick-amount-${val}`}
            >
              {val} EUR
            </button>
          ))}
        </div>

        {/* Amount display */}
        <div className="bg-white dark:bg-card border border-border p-5 sm:p-8 mb-0 text-center" data-testid="amount-display-section">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">Amount</div>
          <div className="amount-display" data-testid="amount-display">
            {amount ? formattedAmount : '0.00'}
            <span className="text-xl sm:text-2xl md:text-3xl ml-2 text-muted-foreground">EUR</span>
          </div>
        </div>

        {/* Keypad */}
        <div className="mb-0">
          <Keypad value={amount} onChange={setAmount} />
        </div>

        {/* Description */}
        <div className="bg-white dark:bg-card border border-border border-t-0 p-3">
          <input
            type="text"
            placeholder="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full h-11 px-3 text-sm bg-muted/50 border border-border focus:outline-none focus:border-orange-500"
            data-testid="payment-description-input"
          />
        </div>

        {/* Fee estimation (owner/admin only) */}
        {(user?.role === 'owner' || user?.role === 'admin') && numericAmount > 0 && feeData && (
          <div className="bg-white dark:bg-card border border-border border-t-0" data-testid="fee-estimation-panel">
            <button
              onClick={() => setShowFees(!showFees)}
              className="w-full flex items-center justify-between px-3 py-2 text-xs text-muted-foreground hover:bg-muted/50 transition-colors"
              data-testid="toggle-fees-btn"
            >
              <span className="font-medium">
                {feeData.promo_active ? 'Promo active - No platform fees' : `Est. fees: ${feeData.total_fees.toFixed(2)} EUR`}
              </span>
              {showFees ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {showFees && (
              <div className="px-3 pb-3 space-y-1.5 text-xs border-t border-border/50 pt-2" data-testid="fee-details">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Platform fee (0.99%)</span>
                  <span className={`font-mono font-medium ${feeData.promo_active ? 'line-through text-muted-foreground' : ''}`}>
                    {feeData.promo_active ? '0.00' : feeData.platform_fee.toFixed(2)} EUR
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Stripe fee (est. 1.5% + 0.25)</span>
                  <span className="font-mono font-medium">{feeData.stripe_fee_estimate.toFixed(2)} EUR</span>
                </div>
                <div className="flex justify-between pt-1.5 border-t border-border/50 font-semibold text-foreground">
                  <span>Net amount</span>
                  <span className="font-mono">{feeData.net_amount.toFixed(2)} EUR</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Charge button */}
        <button onClick={handleCharge} disabled={loading || numericAmount <= 0} className="charge-btn mt-0" data-testid="charge-btn">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
          {loading ? 'Creating...' : `Charge ${formattedAmount} EUR`}
        </button>

        {/* Fixed QR Code Section */}
        {fixedQRUrl && (
          <div className="mt-4">
            <button
              onClick={() => setShowFixedQR(!showFixedQR)}
              className="w-full flex items-center justify-center gap-2 py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground border border-dashed border-border hover:border-foreground/30 transition-colors"
              data-testid="toggle-fixed-qr-btn"
            >
              <QrCode className="w-3.5 h-3.5" />
              {showFixedQR ? 'Hide my permanent QR' : 'My permanent QR code'}
            </button>
            {showFixedQR && (
              <div className="bg-white dark:bg-card border border-border border-t-0 p-5 text-center animate-fade-in-up" data-testid="fixed-qr-section">
                <p className="text-xs text-muted-foreground mb-3">
                  Print this QR and stick it at your checkout. Customers scan it to see your latest pending payment.
                </p>
                <div className="inline-block p-3 bg-white border border-border" id="fixed-qr-container">
                  <QRCodeSVG value={fixedQRUrl} size={160} level="M" includeMargin={false} />
                </div>
                <p className="text-[10px] text-muted-foreground mt-2 font-mono break-all">{fixedQRUrl}</p>
                <div className="flex gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => printQRFromDOM('#fixed-qr-container', null, fixedQRUrl)}
                    className="flex-1 h-9 text-xs font-medium"
                    data-testid="print-fixed-qr-btn"
                  >
                    Print QR
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      await navigator.clipboard.writeText(fixedQRUrl);
                      toast.success('QR link copied!');
                    }}
                    className="flex-1 h-9 text-xs font-medium"
                    data-testid="copy-fixed-qr-btn"
                  >
                    <Copy className="w-3 h-3 mr-1" /> Copy link
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* QR Code Dialog */}
      <Dialog open={showQR} onOpenChange={(open) => { if (!open && paymentStatus === 'paid') resetPayment(); else if (!open) setShowQR(false); }}>
        <DialogContent className="max-w-sm mx-auto p-0 border-0 bg-white dark:bg-card" data-testid="qr-dialog" aria-describedby="qr-dialog-desc">
          <DialogTitle className="sr-only">Payment QR Code</DialogTitle>
          <DialogDescription id="qr-dialog-desc" className="sr-only">Scan the QR code to complete payment</DialogDescription>

          <div className="p-6 text-center">
            {paymentStatus === 'paid' ? (
              <div className="animate-scale-in py-6" data-testid="payment-success-view">
                <CheckCircle className="w-20 h-20 text-emerald-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Payment Received</h2>
                <p className="text-3xl font-extrabold mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{formattedAmount} EUR</p>
                <Button onClick={resetPayment} className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-white font-bold uppercase tracking-wide text-base" data-testid="new-payment-btn">
                  New Payment
                </Button>
              </div>
            ) : paymentStatus === 'failed' ? (
              <div className="animate-scale-in py-6" data-testid="payment-failed-view">
                <XCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Payment Failed</h2>
                <p className="text-sm text-muted-foreground mb-4">The payment session expired or was cancelled.</p>
                <Button onClick={resetPayment} className="w-full h-14 bg-foreground hover:bg-foreground/90 text-background font-bold uppercase tracking-wide" data-testid="try-again-btn">
                  Try Again
                </Button>
              </div>
            ) : (
              <div data-testid="qr-display-view">
                {/* Payment QR (for customer) */}
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-1">Scan to Pay</div>
                <p className="text-3xl font-extrabold mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{formattedAmount} EUR</p>
                <div className="qr-container mx-auto mb-3 animate-scale-in">
                  <QRCodeSVG value={checkoutUrl} size={200} level="M" includeMargin={false} />
                </div>
                <div className="flex items-center justify-center gap-2 text-muted-foreground animate-pulse-ring mb-4">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm font-medium">Waiting for payment...</span>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2 mb-3">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(checkoutUrl);
                        toast.success('Payment link copied!');
                      } catch {
                        const ta = document.createElement('textarea');
                        ta.value = checkoutUrl;
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand('copy');
                        document.body.removeChild(ta);
                        toast.success('Payment link copied!');
                      }
                    }}
                    className="flex-1 h-9 border-border font-medium text-xs"
                    data-testid="copy-link-btn"
                  >
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copy link
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowDisplayLink(!showDisplayLink)}
                    className={`flex-1 h-9 border-border font-medium text-xs ${showDisplayLink ? 'bg-orange-50 dark:bg-orange-950/30 border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-400' : ''}`}
                    data-testid="customer-display-btn"
                  >
                    <Monitor className="w-3.5 h-3.5 mr-1" /> Customer screen
                  </Button>
                </div>

                {/* Customer Display Mode Section */}
                {showDisplayLink && displayUrl && (
                  <div className="bg-slate-50 dark:bg-muted/30 border border-border p-4 mb-3 text-left animate-fade-in-up" data-testid="customer-display-section">
                    <div className="text-xs uppercase tracking-widest text-orange-600 dark:text-orange-400 font-bold mb-3 text-center">
                      Customer Display Mode
                    </div>
                    <p className="text-xs text-muted-foreground mb-3 text-center leading-relaxed">
                      Show the QR code on a separate screen for the customer. Scan the code below with the customer-facing device, or open the link directly.
                    </p>

                    {/* Display URL QR Code */}
                    <div className="flex flex-col items-center gap-3 mb-3">
                      <div className="bg-white p-2 border border-border">
                        <QRCodeSVG value={displayUrl} size={120} level="M" />
                      </div>
                      <div className="text-[10px] text-muted-foreground text-center">
                        Scan with the customer-facing device
                      </div>
                    </div>

                    {/* Action buttons for display */}
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          window.open(displayUrl, 'customer_display', 'width=600,height=900,toolbar=no,menubar=no');
                        }}
                        className="flex-1 h-9 text-xs font-medium"
                        data-testid="open-display-btn"
                      >
                        <Maximize className="w-3 h-3 mr-1" /> Open in window
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          await navigator.clipboard.writeText(displayUrl);
                          toast.success('Display link copied!');
                        }}
                        className="flex-1 h-9 text-xs font-medium"
                        data-testid="copy-display-url-btn"
                      >
                        <Copy className="w-3 h-3 mr-1" /> Copy link
                      </Button>
                    </div>
                  </div>
                )}

                <Button variant="outline" onClick={() => setShowQR(false)} className="w-full h-10 border-border font-medium text-sm" data-testid="cancel-qr-btn">
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
